/**
 * Surface Renaud Google Visibility catalog.
 *
 * Checklist definitions live in code so V1 can track work without Google APIs.
 * Persisted rows only store status/notes/evidence keyed by item id.
 * Future adapters can write evidence without changing this catalog shape.
 */

export const ITEM_STATUS = Object.freeze({
  NOT_STARTED: "not_started",
  IN_PROGRESS: "in_progress",
  COMPLETE: "complete",
  BLOCKED: "blocked",
});

export const ITEM_STATUS_LABELS = Object.freeze({
  [ITEM_STATUS.NOT_STARTED]: "Not Started",
  [ITEM_STATUS.IN_PROGRESS]: "In Progress",
  [ITEM_STATUS.COMPLETE]: "Complete",
  [ITEM_STATUS.BLOCKED]: "Blocked",
});

export const ITEM_STATUS_OPTIONS = Object.freeze([
  { value: ITEM_STATUS.NOT_STARTED, label: ITEM_STATUS_LABELS[ITEM_STATUS.NOT_STARTED] },
  { value: ITEM_STATUS.IN_PROGRESS, label: ITEM_STATUS_LABELS[ITEM_STATUS.IN_PROGRESS] },
  { value: ITEM_STATUS.COMPLETE, label: ITEM_STATUS_LABELS[ITEM_STATUS.COMPLETE] },
  { value: ITEM_STATUS.BLOCKED, label: ITEM_STATUS_LABELS[ITEM_STATUS.BLOCKED] },
]);

export const GATE_STATUS = Object.freeze({
  NOT_STARTED: "not_started",
  IN_PROGRESS: "in_progress",
  PASSED: "passed",
  BLOCKED: "blocked",
});

export const READINESS_STATUS = Object.freeze({
  NOT_READY: "NOT READY",
  GATES_CLEAR: "GATES CLEAR",
});

export const BUSINESS = Object.freeze({
  legalName: "Surface Renaud Inc",
  website: "surfacerenaud.ca",
  websiteUrl: "https://surfacerenaud.ca",
  type: "tile / ceramic installation contractor",
  serviceAreaBusiness: true,
});

export const SECTIONS = Object.freeze([
  {
    id: "google_business_profile",
    title: "Google Business Profile",
    short: "GBP",
    summary:
      "Public local listing, service areas, categories, photos, and review collection.",
  },
  {
    id: "google_search_console",
    title: "Google Search / Search Console",
    short: "Search Console",
    summary:
      "Authoritative indexing and crawl checks for surfacerenaud.ca. Outside-in search is not proof of index status.",
  },
  {
    id: "website_seo",
    title: "Website SEO",
    short: "Website",
    summary:
      "On-site titles, structure, crawlability, and service-area content. This dashboard tracks the work; it does not edit the website.",
  },
  {
    id: "local_visibility",
    title: "Local Visibility & Reputation",
    short: "Local",
    summary:
      "Citation consistency, residential-address exposure, and ongoing review/photo hygiene.",
  },
]);

export const GATES = Object.freeze([
  {
    id: "gbp_configured",
    title: "Google Business Profile configured correctly",
    summary:
      "Official name, website, hidden residential address, service areas, primary category, phone, and hours.",
    itemIds: [
      "gbp_complete_profile",
      "gbp_confirm_name",
      "gbp_confirm_website",
      "gbp_hide_residential_address",
      "gbp_service_areas",
      "gbp_primary_category",
      "gbp_confirm_phone",
      "gbp_confirm_hours",
    ],
  },
  {
    id: "search_console_verified",
    title: "Search Console verified",
    summary: "surfacerenaud.ca is a verified Search Console property with confirmed domain ownership.",
    itemIds: ["gsc_verify_property", "gsc_confirm_ownership"],
  },
  {
    id: "sitemap_submitted",
    title: "Sitemap submitted",
    summary: "Sitemap submitted in Search Console for the production site.",
    itemIds: ["gsc_submit_sitemap"],
  },
  {
    id: "indexing_resolved",
    title: "Critical indexing/crawl issues resolved",
    summary:
      "Search Console inspection is the authority. Do not treat an outside-in web search miss as proof the site is unindexed.",
    itemIds: [
      "gsc_indexing_status",
      "gsc_resolve_indexing",
      "gsc_crawl_production",
    ],
  },
  {
    id: "core_website_seo",
    title: "Core website SEO reviewed",
    summary: "Titles, meta descriptions, canonicals, robots.txt, and crawlability reviewed.",
    itemIds: [
      "seo_page_titles",
      "seo_meta_descriptions",
      "seo_canonical_urls",
      "seo_robots_txt",
      "seo_crawlability",
    ],
  },
  {
    id: "citations_consistent",
    title: "Business identity/citations reasonably consistent",
    summary:
      "Surface Renaud Inc, surfacerenaud.ca, and contact details match; residential address is not publicly exposed where it can be corrected.",
    itemIds: [
      "local_standardize_name",
      "local_standardize_website",
      "local_standardize_phone",
      "local_identify_residential",
      "local_remove_residential",
    ],
  },
]);

/** @type {ReadonlyArray<{id: string, sectionId: string, title: string, summary: string, recommendedAction: string}>} */
export const ITEMS = Object.freeze([
  {
    id: "gbp_complete_profile",
    sectionId: "google_business_profile",
    title: "Complete Google Business Profile",
    summary: "Fill required GBP fields so the listing can appear as a complete service-area business.",
    recommendedAction: "Walk the GBP checklist in Google Business Profile Manager and mark remaining gaps here.",
  },
  {
    id: "gbp_confirm_name",
    sectionId: "google_business_profile",
    title: "Confirm official business name: Surface Renaud Inc",
    summary: "Public name must match the legal business identity used on the website and citations.",
    recommendedAction: "Set the GBP name to Surface Renaud Inc. Do not use informal variants.",
  },
  {
    id: "gbp_confirm_website",
    sectionId: "google_business_profile",
    title: "Confirm website: surfacerenaud.ca",
    summary: "GBP website should point at the production domain, not a social profile or staging URL.",
    recommendedAction: "Set the website to https://surfacerenaud.ca.",
  },
  {
    id: "gbp_hide_residential_address",
    sectionId: "google_business_profile",
    title: "Hide residential street address from public profile",
    summary: "This is a service-area business. The residential/business street address should not be public.",
    recommendedAction: "Use service-area mode and keep the street address hidden from the public listing.",
  },
  {
    id: "gbp_service_areas",
    sectionId: "google_business_profile",
    title: "Configure correct service areas",
    summary: "Service areas should match where Surface Renaud actually takes tile/ceramic work.",
    recommendedAction: "Add/confirm cities and regions served. Remove areas that are not real coverage.",
  },
  {
    id: "gbp_primary_category",
    sectionId: "google_business_profile",
    title: "Verify primary business category",
    summary: "Primary category should describe tile / ceramic installation, not a generic contractor bucket.",
    recommendedAction: "Choose the closest Google category for tile or ceramic installation and confirm it in GBP.",
  },
  {
    id: "gbp_secondary_categories",
    sectionId: "google_business_profile",
    title: "Add appropriate secondary categories",
    summary: "Secondary categories help adjacent searches without diluting the primary trade.",
    recommendedAction: "Add a small set of relevant secondary categories only.",
  },
  {
    id: "gbp_complete_services",
    sectionId: "google_business_profile",
    title: "Complete services",
    summary: "GBP services should reflect actual tile/ceramic installation offerings.",
    recommendedAction: "Enter the live service list. Keep names consistent with the website.",
  },
  {
    id: "gbp_optimize_description",
    sectionId: "google_business_profile",
    title: "Optimize business description",
    summary: "Description should state who Surface Renaud is, what they install, and where they work.",
    recommendedAction: "Write a factual description using Surface Renaud Inc and surfacerenaud.ca.",
  },
  {
    id: "gbp_confirm_phone",
    sectionId: "google_business_profile",
    title: "Confirm phone/contact information",
    summary: "Public phone must match the number used on the website and citations.",
    recommendedAction: "Confirm the GBP phone against the official Surface Renaud contact number.",
  },
  {
    id: "gbp_confirm_hours",
    sectionId: "google_business_profile",
    title: "Confirm business hours",
    summary: "Hours should match how the business actually answers the phone and books work.",
    recommendedAction: "Set and verify regular hours in GBP.",
  },
  {
    id: "gbp_project_photos",
    sectionId: "google_business_profile",
    title: "Add quality project/work photos",
    summary: "Photos of completed tile/ceramic work outperform generic stock images.",
    recommendedAction: "Upload recent, well-lit project photos. Avoid exposing the residential address in captions.",
  },
  {
    id: "gbp_logo",
    sectionId: "google_business_profile",
    title: "Add logo/branding where appropriate",
    summary: "Profile logo/cover should match Surface Renaud branding.",
    recommendedAction: "Upload the current logo to the GBP profile photo.",
  },
  {
    id: "gbp_social_profiles",
    sectionId: "google_business_profile",
    title: "Add available social profiles",
    summary: "Link only profiles that are actually used for the business.",
    recommendedAction: "Add Facebook/Instagram/etc. if they exist and are current.",
  },
  {
    id: "gbp_review_collection",
    sectionId: "google_business_profile",
    title: "Establish Google review collection process",
    summary: "There should be a repeatable way to ask completed customers for a Google review.",
    recommendedAction: "Define who asks, when, and with which short link. Do not buy reviews.",
  },
  {
    id: "gsc_verify_property",
    sectionId: "google_search_console",
    title: "Verify surfacerenaud.ca in Google Search Console",
    summary: "Search Console is the authoritative place to inspect indexing for this domain.",
    recommendedAction: "Add and verify the production property for surfacerenaud.ca.",
  },
  {
    id: "gsc_confirm_ownership",
    sectionId: "google_search_console",
    title: "Confirm domain ownership",
    summary: "Prefer domain-wide verification so www and non-www are covered.",
    recommendedAction: "Complete DNS or equivalent domain verification and confirm it stays valid.",
  },
  {
    id: "gsc_indexing_status",
    sectionId: "google_search_console",
    title: "Check current Google indexing status",
    summary:
      "An outside-in Google/web search did not reliably surface surfacerenaud.ca pages. Treat Search Console as the source of truth.",
    recommendedAction: "Use Pages / URL Inspection in Search Console. Do not mark this complete from a public search test alone.",
  },
  {
    id: "gsc_submit_sitemap",
    sectionId: "google_search_console",
    title: "Submit sitemap",
    summary: "Google needs a submitted sitemap to discover important URLs.",
    recommendedAction: "Submit the production sitemap URL in Search Console and confirm it is processed without errors.",
  },
  {
    id: "gsc_inspect_homepage",
    sectionId: "google_search_console",
    title: "Inspect homepage indexing",
    summary: "The homepage should be requested and eligible for serving.",
    recommendedAction: "URL-inspect https://surfacerenaud.ca/ and record the Search Console verdict here.",
  },
  {
    id: "gsc_inspect_service_pages",
    sectionId: "google_search_console",
    title: "Inspect important service pages",
    summary: "Key service URLs should be inspected, not assumed indexed.",
    recommendedAction: "URL-inspect the main tile/ceramic service pages and note exclusions.",
  },
  {
    id: "gsc_resolve_indexing",
    sectionId: "google_search_console",
    title: "Resolve indexing/exclusion problems",
    summary: "noindex, robots blocks, canonical conflicts, and soft 404s must be cleared for critical URLs.",
    recommendedAction: "Fix whatever Search Console reports. Leave blocked if waiting on website changes.",
  },
  {
    id: "gsc_request_indexing",
    sectionId: "google_search_console",
    title: "Request indexing where appropriate",
    summary: "Request indexing only after crawl/index blockers are understood.",
    recommendedAction: "Request indexing for the homepage and key service pages after inspection.",
  },
  {
    id: "gsc_crawl_production",
    sectionId: "google_search_console",
    title: "Confirm Google can crawl the production site",
    summary: "robots.txt, HTTP status, and hosting must allow Googlebot on production.",
    recommendedAction: "Use Search Console robots and URL inspection. Confirm production, not staging.",
  },
  {
    id: "seo_page_titles",
    sectionId: "website_seo",
    title: "Audit page titles",
    summary: "Each important page should have a unique, accurate title including the business or service.",
    recommendedAction: "Review homepage and service-page titles. Track website changes elsewhere — do not edit the site from CrewPilot.",
  },
  {
    id: "seo_meta_descriptions",
    sectionId: "website_seo",
    title: "Audit meta descriptions",
    summary: "Meta descriptions should be unique and usable as search snippets.",
    recommendedAction: "Review homepage and service-page descriptions for accuracy and uniqueness.",
  },
  {
    id: "seo_heading_hierarchy",
    sectionId: "website_seo",
    title: "Audit H1/H2 hierarchy",
    summary: "Each page should have a clear H1 and supporting H2s.",
    recommendedAction: "Spot-check heading structure on homepage and service pages.",
  },
  {
    id: "seo_canonical_urls",
    sectionId: "website_seo",
    title: "Confirm canonical URLs",
    summary: "Canonicals should point at the preferred production URLs.",
    recommendedAction: "Confirm self-canonicals on key pages and a single preferred host (surfacerenaud.ca).",
  },
  {
    id: "seo_robots_txt",
    sectionId: "website_seo",
    title: "Verify robots.txt",
    summary: "robots.txt must allow important pages and may point to the sitemap.",
    recommendedAction: "Open https://surfacerenaud.ca/robots.txt and confirm it is not blocking the site.",
  },
  {
    id: "seo_sitemap_xml",
    sectionId: "website_seo",
    title: "Verify sitemap.xml",
    summary: "A sitemap should exist, be valid, and list the important production URLs.",
    recommendedAction: "Open the production sitemap and confirm it matches live service pages.",
  },
  {
    id: "seo_crawlability",
    sectionId: "website_seo",
    title: "Check crawlability",
    summary: "Important pages should return 200, not require login, and not be noindexed by accident.",
    recommendedAction: "Spot-check status codes and indexability for homepage and service pages.",
  },
  {
    id: "seo_structured_data",
    sectionId: "website_seo",
    title: "Add/verify appropriate structured data",
    summary: "LocalBusiness / contractor schema can help, but only if it matches public facts and hides the residential address.",
    recommendedAction: "Review JSON-LD. Do not publish the residential street address.",
  },
  {
    id: "seo_nap_consistency",
    sectionId: "website_seo",
    title: "Ensure business name/contact information is consistent",
    summary: "Website NAP must match GBP and citations: Surface Renaud Inc and surfacerenaud.ca.",
    recommendedAction: "Compare footer/contact content to the official name, website, and phone.",
  },
  {
    id: "seo_service_pages",
    sectionId: "website_seo",
    title: "Review service-page content",
    summary: "Service pages should describe real tile/ceramic work in plain language.",
    recommendedAction: "Review whether each offered service has a useful page.",
  },
  {
    id: "seo_geo_content",
    sectionId: "website_seo",
    title: "Review geographic/service-area content",
    summary: "The site should say where Surface Renaud works without inventing coverage.",
    recommendedAction: "Review service-area copy against the GBP service areas.",
  },
  {
    id: "seo_mobile_health",
    sectionId: "website_seo",
    title: "Mobile SEO/basic technical health",
    summary: "The production site should be usable on phones and load without blocking errors.",
    recommendedAction: "Spot-check mobile layout, HTTPS, and obvious technical errors.",
  },
  {
    id: "seo_internal_linking",
    sectionId: "website_seo",
    title: "Internal linking",
    summary: "Homepage and service pages should link to each other in a way Google can follow.",
    recommendedAction: "Confirm important pages are reachable from the main navigation.",
  },
  {
    id: "seo_image_alt",
    sectionId: "website_seo",
    title: "Image alt text where appropriate",
    summary: "Project photos should have descriptive alt text without stuffing.",
    recommendedAction: "Spot-check alt text on homepage and gallery/project images.",
  },
  {
    id: "local_citations_audit",
    sectionId: "local_visibility",
    title: "Audit third-party business listings/citations",
    summary: "Find directories that already list Surface Renaud and note mismatches.",
    recommendedAction: "Search major Canadian directories and record name/website/phone/address variants.",
  },
  {
    id: "local_standardize_name",
    sectionId: "local_visibility",
    title: "Standardize official spelling to Surface Renaud Inc",
    summary: "Citations should not drift into SurfaceRenaud, Surface Renault, etc.",
    recommendedAction: "Correct the public name wherever an edit form exists.",
  },
  {
    id: "local_standardize_website",
    sectionId: "local_visibility",
    title: "Standardize website to surfacerenaud.ca",
    summary: "Listings should use the production domain, not social URLs or dead sites.",
    recommendedAction: "Replace incorrect website fields with https://surfacerenaud.ca.",
  },
  {
    id: "local_standardize_phone",
    sectionId: "local_visibility",
    title: "Standardize phone/contact information",
    summary: "One official phone across GBP, website, and citations.",
    recommendedAction: "Align listed phone numbers to the official Surface Renaud number.",
  },
  {
    id: "local_identify_residential",
    sectionId: "local_visibility",
    title: "Identify listings exposing the residential address",
    summary: "Some aggregators may still show a street address that should stay private.",
    recommendedAction: "List every public source that shows the residential/business street address.",
  },
  {
    id: "local_remove_residential",
    sectionId: "local_visibility",
    title: "Correct/remove public residential address where possible",
    summary: "Service-area businesses should not keep a public home street address on listings.",
    recommendedAction: "Request edits/removals. Mark blocked if a directory cannot hide the address.",
  },
  {
    id: "local_add_website_links",
    sectionId: "local_visibility",
    title: "Add missing website links to relevant listings",
    summary: "Useful directories should point back to surfacerenaud.ca.",
    recommendedAction: "Add the website on listings that are worth keeping.",
  },
  {
    id: "local_review_volume",
    sectionId: "local_visibility",
    title: "Build Google review volume",
    summary: "Steady genuine reviews from completed jobs matter more than a one-time blast.",
    recommendedAction: "Run the review-collection process after completed projects.",
  },
  {
    id: "local_respond_reviews",
    sectionId: "local_visibility",
    title: "Respond to Google reviews",
    summary: "Public replies show the business is active.",
    recommendedAction: "Reply to existing Google reviews and keep a simple response habit.",
  },
  {
    id: "local_recent_photos",
    sectionId: "local_visibility",
    title: "Maintain recent project photos",
    summary: "GBP should not stall on old photos.",
    recommendedAction: "Upload new project photos as jobs complete.",
  },
  {
    id: "local_gbp_updates",
    sectionId: "local_visibility",
    title: "Periodically publish appropriate Google Business Profile updates",
    summary: "Occasional GBP posts/updates keep the listing active without turning into a marketing CMS.",
    recommendedAction: "Publish a short update when a notable project wraps, if appropriate.",
  },
]);

export const ITEM_BY_ID = Object.freeze(
  Object.fromEntries(ITEMS.map((item) => [item.id, item])),
);

export const SECTION_BY_ID = Object.freeze(
  Object.fromEntries(SECTIONS.map((section) => [section.id, section])),
);

export const GATE_BY_ID = Object.freeze(
  Object.fromEntries(GATES.map((gate) => [gate.id, gate])),
);

export const GATE_ITEM_ID_SET = Object.freeze(
  new Set(GATES.flatMap((gate) => gate.itemIds)),
);

export function isItemStatus(value) {
  return Object.values(ITEM_STATUS).includes(value);
}

export function normalizeItemStatus(value) {
  return isItemStatus(value) ? value : ITEM_STATUS.NOT_STARTED;
}
